/**
 * Centralized order styling definitions for consistent appearance across all components
 * Used by: Chart orders, OpenOrdersPanel, Positions, OperationSidebar, etc.
 */

/**
 * Get human-readable order side text in Portuguese
 * @param {string} side - Order side (buy/sell)
 * @returns {string} Formatted side text
 */
export const getOrderSideText = (side) => {
  const sideStr = String(side || "").toLowerCase();
  switch (sideStr) {
    case "buy":
      return "COMPRA";
    case "sell":
      return "VENDA";
    default:
      return "UNK";
  }
};

/**
 * Translate broker/OMS execution-report status text (FIX tag 58) to Portuguese.
 * The broker sends some lifecycle acks in English (e.g. stop-limit triggers) that
 * would otherwise show up untranslated in the order log panel (#364). Matching is
 * case-insensitive on the trimmed text; unknown messages pass through unchanged.
 * @param {string} message - raw broker status text (report.message)
 * @returns {string} Portuguese text, or the original if no mapping exists
 */
const BROKER_MESSAGE_PT = {
  "stop limit order received.": "Ordem stop limit recebida.",
  "stop limit order accepted by oms.": "Ordem stop limit aceita pelo OMS.",
  "stop limit order was triggered.": "Stop limit disparado.",
  "stop limit was triggered and sent to exchange.":
    "Stop limit disparado e enviado à bolsa.",
};
export const translateBrokerMessage = (message) => {
  if (!message || typeof message !== "string") return message;
  return BROKER_MESSAGE_PT[message.trim().toLowerCase()] || message;
};

/**
 * Turns a raw broker rejection reason into a user-facing explanation.
 * The router's "Indicação Photon inválida. Preço ultrassou entrada sugerida."
 * is jargon; a crossed OTO entry is the real cause. Falls back to the raw reason
 * (broker text is already PT) so nothing meaningful is hidden.
 * @param {string} reason - report.reason || report.message
 * @returns {string} - friendly reason, or the raw one when unmapped
 */
export const translateRejectionReason = (reason) => {
  if (!reason || typeof reason !== "string") return reason;
  const r = reason.toLowerCase();
  if (r.includes("photon") || r.includes("entrada sugerida")) {
    return "A entrada da ordem OTO cruzou o mercado (compra acima / venda abaixo do preço atual). Ajuste o preço da entrada ou envie a mercado.";
  }
  return reason;
};

// Order side colors (primary color scheme)
export const ORDER_SIDE_COLORS = {
  buy: "#2196F3", // Blue for buy orders
  sell: "#F44336", // Red for sell orders
  default: "#607D8B", // Default gray for unknown sides
};

// UI element colors for order components
export const ORDER_UI_COLORS = {
  background: "#2c3e50", // Dark blue-gray background for order areas
  textPrimary: "#ffffff", // White text for high contrast
  textShadow: "rgba(0, 0, 0, 0.8)", // Black shadow for text readability
  trashButton: "#ff0000", // Orange for delete/trash buttons
  sendButton: "#2ecc71", // Green for send/submit buttons
  stopsButton: "#4A90E2", // Blue for stops button

  // Professional order type badges
  orderTypeBadge: {
    limit: {
      buy: {
        background: "#1976D2", // Darker blue for buy LIMIT orders (matches ORDER_SIDE_COLORS.buy but darker)
        text: "#ffffff", // White text
        border: "#1565C0", // Even darker blue border
        shadow: "rgba(25, 118, 210, 0.4)",
      },
      sell: {
        background: "#D32F2F", // Darker red for sell LIMIT orders (matches ORDER_SIDE_COLORS.sell but darker)
        text: "#ffffff", // White text
        border: "#C62828", // Even darker red border
        shadow: "rgba(211, 47, 47, 0.4)",
      },
    },
    stopLimit: {
      buy: {
        background: "#1976D2", // Darker blue for buy STOP LIMIT orders (matches ORDER_SIDE_COLORS.buy but darker)
        text: "#ffffff", // White text
        border: "#1565C0", // Even darker blue border
        shadow: "rgba(25, 118, 210, 0.4)",
      },
      sell: {
        background: "#D32F2F", // Darker red for sell STOP LIMIT orders (matches ORDER_SIDE_COLORS.sell but darker)
        text: "#ffffff", // White text
        border: "#C62828", // Even darker red border
        shadow: "rgba(211, 47, 47, 0.4)",
      },
    },
    market: {
      background: "#6c757d", // Gray for MARKET orders
      text: "#ffffff", // White text
      border: "#495057", // Darker gray border
      shadow: "rgba(108, 117, 125, 0.4)",
    },
  },
};

// Order display settings
export const ORDER_DISPLAY_SETTINGS = {
  alignRight: true, // Align order text to the right side of the chart (near Y-axis)
  textOffsetFromEdge: 10, // Pixels from the right edge

  // Dynamic width calculation settings
  basePadding: 16, // Base padding for text (8px on each side)
  minWidth: 50, // Minimum width for rectangles
  charWidth: 7, // Approximate character width for SF Pro Display at 10-11px
  statePadding: 12, // Padding for state text section
};

/**
 * Calculate dynamic width for order text background
 * @param {string} text - The text to measure
 * @param {number} fontSize - Font size (default 10)
 * @param {number} basePadding - Base padding around text
 * @returns {number} Calculated width in pixels
 */
export const calculateTextWidth = (
  text,
  fontSize = 10,
  basePadding = ORDER_DISPLAY_SETTINGS.basePadding,
) => {
  if (!text) return ORDER_DISPLAY_SETTINGS.minWidth;

  // Approximate character width based on font size
  // SF Pro Display is relatively condensed, so we use a smaller multiplier
  const charWidth = fontSize * 0.65;
  const textWidth = text.length * charWidth;
  const totalWidth = textWidth + basePadding;

  return Math.max(totalWidth, ORDER_DISPLAY_SETTINGS.minWidth);
};

/**
 * Calculate widths for order display sections with nested quantity area
 * This creates a three-section design:
 * - Main background contains: [SIDE_TEXT] [gap] [QUANTITY_BACKGROUND]
 * - "COMPRA" + gap + nested quantity area = ~98px main width
 * - "VENDA" + gap + nested quantity area = ~93px main width
 * - Quantity backgrounds: "1" = ~25px, "100" = ~35px, "100.000" = ~55px
 * - State section: "LOCAL" = ~55px, "PENDENTE" = ~70px
 * @param {string} side - Order side ('buy' or 'sell')
 * @param {string} state - Order state
 * @param {number} quantity - Order quantity
 * @returns {object} Object with mainWidth, quantityWidth, and secondaryWidth
 */
export const calculateOrderWidths = (side, state, quantity) => {
  const sideText = getOrderSideText(side);
  const stateText = getOrderStateText(state);
  const quantityText = String(quantity || 0);

  // Calculate individual section widths
  const sideTextWidth = calculateTextWidth(sideText, 11, 12); // Less padding for side text only
  const quantityWidth = calculateTextWidth(quantityText, 10, 10); // Compact quantity area
  const secondaryWidth = calculateTextWidth(
    stateText,
    10,
    ORDER_DISPLAY_SETTINGS.statePadding,
  );

  // Main width includes side text + quantity area + gap
  const mainWidth = sideTextWidth + quantityWidth + 13; // 13px gap between side and quantity for better visual spacing

  return {
    mainWidth,
    sideTextWidth,
    quantityWidth,
    secondaryWidth,
  };
};

// Order state styling patterns
export const ORDER_STATE_STYLES = {
  local: {
    strokeDasharray: "1,2",
    emoji: "",
    opacity: 0.7,
    blur: 4, // 4px blur for local orders
  },
  pending: {
    strokeDasharray: "1,2",
    emoji: "",
    opacity: 0.6,
    blur: 4, // 8px blur for pending orders (more pronounced)
  },
  open: {
    strokeDasharray: "none",
    emoji: "",
    opacity: 1.0,
    blur: 0, // No blur for open orders
  },
  filled: {
    strokeDasharray: "none",
    emoji: "",
    opacity: 1.0,
    blur: 0, // No blur for filled orders
  },
  cancelled: {
    strokeDasharray: "2,2",
    emoji: "",
    opacity: 1.0,
    blur: 2, // Slight blur for cancelled orders
  },
  rejected: {
    strokeDasharray: "1,1",
    emoji: "",
    opacity: 1.0,
    blur: 3, // Moderate blur for rejected orders
  },
  default: {
    strokeDasharray: "none",
    emoji: "",
    opacity: 1.0,
    blur: 0, // No blur by default
  },
};

/**
 * Get the color for an order based on its side
 * @param {string} side - 'buy' or 'sell'
 * @returns {string} Hex color code
 */
export const getOrderSideColor = (side) => {
  return ORDER_SIDE_COLORS[side?.toLowerCase()] || ORDER_SIDE_COLORS.default;
};

/**
 * Get the style configuration for an order based on its state
 * @param {string} state - Order state (local, pending, open, filled, cancelled, rejected)
 * @returns {object} Style configuration object
 */
export const getOrderStateStyle = (state) => {
  return ORDER_STATE_STYLES[state?.toLowerCase()] || ORDER_STATE_STYLES.default;
};

/**
 * Get complete styling for an order (combines side color and state patterns)
 * @param {string} side - 'buy' or 'sell'
 * @param {string} state - Order state
 * @returns {object} Complete style configuration
 */
export const getOrderStyle = (side, state) => {
  const sideColor = getOrderSideColor(side);
  const stateStyle = getOrderStateStyle(state);

  return {
    color: sideColor,
    backgroundColor: sideColor,
    stroke: sideColor,
    fill: sideColor,
    ...stateStyle,
  };
};

/**
 * Get human-readable order state text with full names
 * @param {string} state - Order state
 * @returns {string} Formatted state text
 */
export const getOrderStateText = (state) => {
  switch (state?.toLowerCase()) {
    case "local":
      return "LOCAL";
    case "pending":
      return "PENDING";
    case "open":
      return "OPEN";
    case "filled":
      return "FILLED";
    case "cancelled":
      return "CANCELLED";
    case "rejected":
      return "REJECTED";
    default:
      return "UNKNOWN";
  }
};

/**
 * Get order display text with professional formatting
 * @param {string} side - 'buy' or 'sell'
 * @param {string} state - Order state
 * @param {number} quantity - Order quantity
 * @returns {string} Formatted display text
 */
export const getOrderDisplayText = (side, state, quantity) => {
  const sideText = getOrderSideText(side);
  const stateText = getOrderStateText(state);

  return `${sideText} ${stateText} (${quantity || 0})`;
};

/**
 * CSS class names for order styling (for use in components)
 */
export const ORDER_CSS_CLASSES = {
  buy: "order-side-buy",
  sell: "order-side-sell",
  local: "order-state-local",
  pending: "order-state-pending",
  open: "order-state-open",
  filled: "order-state-filled",
  cancelled: "order-state-cancelled",
  rejected: "order-state-rejected",
};

/**
 * Get CSS class names for an order
 * @param {string} side - 'buy' or 'sell'
 * @param {string} state - Order state
 * @returns {string} Space-separated CSS class names
 */
export const getOrderCssClasses = (side, state) => {
  const sideClass = ORDER_CSS_CLASSES[side?.toLowerCase()] || "";
  const stateClass = ORDER_CSS_CLASSES[state?.toLowerCase()] || "";
  return `${sideClass} ${stateClass}`.trim();
};

// Export all constants for direct access if needed
export default {
  ORDER_SIDE_COLORS,
  ORDER_UI_COLORS,
  ORDER_STATE_STYLES,
  ORDER_CSS_CLASSES,
  ORDER_DISPLAY_SETTINGS,
  getOrderSideColor,
  getOrderStateStyle,
  getOrderStyle,
  getOrderSideText,
  getOrderStateText,
  getOrderDisplayText,
  getOrderCssClasses,
  calculateTextWidth,
  calculateOrderWidths,
};
